import React from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { MapPin, Phone, Mail } from 'lucide-react';

function InstagramIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
    </svg>
  );
}

function FacebookIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
    </svg>
  );
}

export default function GuestFooter() {
  return (
    <footer className="bg-foreground text-white">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <AppLogo size={36} />
              <span className="font-extrabold text-xl tracking-tight">
                Vacay<span className="text-accent">Rent</span>
              </span>
            </div>
            <p className="text-sm text-white/60 leading-relaxed">
              Four handpicked vacation apartments in the heart of the Costa Brava. Your perfect escape awaits.
            </p>
            <div className="flex items-center gap-3">
              <a href="https://instagram.com" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary transition-colors">
                <InstagramIcon size={16} />
              </a>
              <a href="https://facebook.com" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary transition-colors">
                <FacebookIcon size={16} />
              </a>
            </div>
          </div>

          {/* Apartments */}
          <div>
            <h4 className="font-700 text-sm uppercase tracking-wider mb-4 text-white/80">Our Apartments</h4>
            <ul className="space-y-2.5">
              {['Sunset Terrace Suite', 'Ocean Breeze Loft', 'Garden Villa Escape', 'Rooftop Haven']?.map((name) => (
                <li key={`footer-apt-${name}`}>
                  <Link href="/apartment-detail" className="text-sm text-white/60 hover:text-accent transition-colors">
                    {name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-700 text-sm uppercase tracking-wider mb-4 text-white/80">Quick Links</h4>
            <ul className="space-y-2.5">
              {[
                { label: 'Check Availability', href: '/' },
                { label: 'Apartment Detail', href: '/apartment-detail' },
                { label: 'Cancellation Policy', href: '#' },
                { label: 'House Rules', href: '#' },
                { label: 'Privacy Policy', href: '#' },
              ]?.map((link) => (
                <li key={`footer-link-${link?.label}`}>
                  <Link href={link?.href} className="text-sm text-white/60 hover:text-accent transition-colors">
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-700 text-sm uppercase tracking-wider mb-4 text-white/80">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2.5 text-sm text-white/60">
                <MapPin size={15} className="shrink-0 mt-0.5 text-accent" />
                Carrer del Mar 42, Lloret de Mar, Girona, Spain
              </li>
              <li className="flex items-center gap-2.5 text-sm text-white/60">
                <Phone size={15} className="shrink-0 text-accent" />
                +34 600 123 456
              </li>
              <li className="flex items-center gap-2.5 text-sm text-white/60">
                <Mail size={15} className="shrink-0 text-accent" />
                hello@vacayrent.es
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/40">
            © 2026 VacayRent. All rights reserved. Payments powered by Stripe.
          </p>
          <div className="flex items-center gap-4">
            <span className="text-xs text-white/40">Terms</span>
            <span className="text-xs text-white/40">Privacy</span>
            <span className="text-xs text-white/40">Cookies</span>
          </div>
        </div>
      </div>
    </footer>
  );
}